import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';

import {
  LocationSummary,
  LocationSummaryReport,
} from 'src/app/core/models/reports/reports.model';
import { SummaryReportsService } from 'src/app/core/services/reports/summary/summary-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  CONSTS,
  LOCATION_PDF_REPORT,
  LOCATION_REPORT_FILE_NAME,
  LocationSummaryFormGroup,
} from './location-summary.constants';

@Component({
  selector: 'app-location-summary',
  templateUrl: './location-summary.component.html',
  styleUrls: ['./location-summary.component.scss'],
  standalone: false,
})
export class LocationSummaryComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly userService = inject(UserService);
  data: LocationSummaryFormGroup = inject(MAT_DIALOG_DATA);
  private dialogRef: MatDialogRef<LocationSummaryComponent> = inject(
    MatDialogRef<LocationSummaryComponent>,
  );
  private fb: FormBuilder = inject(FormBuilder);
  private summaryReportsService: SummaryReportsService = inject(
    SummaryReportsService,
  );
  private reportUtils: ReportsUtils = inject(ReportsUtils);
  private commonUtils: CommonUtils = inject(CommonUtils);

  readonly CONSTS = CONSTS;
  readonly IMAGES = IMAGE_SRC;
  readonly OPTIONS = Options;
  readonly PDF_REPORT = LOCATION_PDF_REPORT;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  locationSummaryForm!: FormGroup;
  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;

  summary!: LocationSummary[];
  locationSummaryReport!: LocationSummaryReport;
  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  ngOnInit(): void {
    this.currDate = new Date();
    this.minDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 30,
    );
    this.maxDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() + 14,
    );

    this.locationSummaryForm = this.fb.group({
      date: [this.currDate],
      reportFormat: [Options.PDF],
    });
  }

  onExit() {
    this.commonUtils.openExitModal(this.locationSummaryForm.dirty);
  }

  radioChange(newValue: Options) {
    this.locationSummaryForm.get('reportFormat')?.setValue(newValue);
  }

  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof LocationSummaryComponent
   */
  getReport() {
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          const formValue = this.locationSummaryForm.value;

          const locationcode = user?.location;
          const completeddate = this.commonUtils.getFormattedDate(
            formValue.date,
          );

          const request = {
            locationcode,
            completeddate,
          };
          return this.summaryReportsService
            .getLocationSummaryReport(request)
            .pipe(
              take(1),
              map((data) => {
                const formatType =
                  this.locationSummaryForm.get('reportFormat')?.value;
                const summary = data.response.summary;
                if (summary?.length) {
                  if (formatType === Options.CSV) {
                    const customHeader = [
                      [
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        'LOCATION SUMMARY',
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                      ],
                      [
                        '',
                        '',
                        '',
                        '',
                        '',
                        `${data.response.locationCode}`,
                        '',
                        '',
                        `${data.response.locationName}`,
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        '',
                        `${data.response.date}`,
                      ],
                    ];
                    const formatedDate = formatDate(
                      new Date(),
                      'MM-dd-yyyy',
                      'en-US',
                    );
                    const customFooter = [
                      [
                        'TOTAL',
                        '',
                        `${data?.response.totalStops}`,
                        `${data?.response.totalPkgs}`,
                        `${data?.response.totalDeliveryStopsNET}`,
                        `${data?.response.totalDeliveryPkgsNET}`,
                        `${data?.response.totalPickupStopsNET}`,
                        `${data?.response.totalPickupPkgsNET}`,
                        `${data?.response.totalMissedPickup}`,
                        `${data?.response.totalMissedDelStop}`,
                        `${data?.response.totalMissedDelPkgs}`,
                        `${data?.response.totalSendAgainCOD}`,
                        `${data?.response.totalSendAgainPaid}`,
                        `${data?.response.totalNetCOD}`,
                        `${data?.response.totalOffload}`,
                        `${data?.response.totalOpen}`,
                        `${data?.response.totalOther}`,
                      ],
                      ['Printed:', `${formatedDate}`],
                    ];
                    this.reportUtils.exportCSVReport(
                      summary,
                      LOCATION_REPORT_FILE_NAME,
                      customHeader,
                      customFooter,
                    );
                  }
                  if (formatType === Options.PDF) {
                    this.summary = data.response.summary;
                    this.locationSummaryReport = data;
                    setTimeout(() => {
                      const content =
                        this.pdfContainer.element.nativeElement.innerHTML;
                      this.reportUtils.exportPdfReport(
                        content,
                        LOCATION_PDF_REPORT.NAME,
                        LOCATION_REPORT_FILE_NAME,
                        'l',
                      );
                    }, 0);
                  }
                } else {
                  this.commonUtils.updateBannerType(
                    BANNER_TYPE.NO_DATA_AVAILABLE,
                  );
                }

                this.dialogRef.close();
              }),
            );
        }),
      )
      .subscribe();
  }
}
